import _ from 'lodash';
import classnames from 'classnames';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { translationPropType } from '@eb/i18n';
import { Icon } from '@eb/eds-icon';
import { Button } from '@eb/eds-button';
import { Feature } from './features';
import BasePackage from './BasePackage';
import Pricing from './Pricing';
import {
    PRICING_PROP_TYPES,
    PRICING_PROPS,
} from '../../constants/componentPropTypes';
import { ASSORTMENT_TAX_PROP_TYPES } from '../../constants/pricingPropTypes';
import './package.scss';

import { ArrowRightChunky } from '@eb/eds-iconography';

const PackageFeatures = ({ features, useMain, shouldShowNewTag }) => {
    const featureItems = features.map((value, index) => {
        const main = useMain && index === 0;

        return (
            <div key={`${value.toString()}`} className="eds-l-pad-vert-1">
                <Feature
                    name={value}
                    main={main}
                    shouldShowNewTag={shouldShowNewTag}
                />
            </div>
        );
    });

    return <div>{featureItems}</div>;
};

const PackageDetail = ({
    features,
    includeOtherFeatures,
    onClick,
    primaryCTA,
    copyPrimaryCTA,
    isCopy,
    CTAStyle,
    dataAutomation,
    pricingProps,
    taxLabel,
    shouldShowNewTag,
}) => {
    let packageCTA = primaryCTA;

    if (isCopy && copyPrimaryCTA) {
        packageCTA = copyPrimaryCTA;
    }

    return (
        <div className="eds-l-pad-bot-4 eds-l-pad-hor-6 eds-l-ln-pad-hor-10 eds-l-lg-pad-hor-10 eds-l-lw-pad-hor-10">
            <div className="eds-g-cell eds-g-cell-12-12 eds-g-cell--has-overflow eds-align--center">
                <Button
                    style={CTAStyle}
                    size="block"
                    onClick={onClick}
                    disabled={!CTAStyle}
                    data-automation={dataAutomation}
                >
                    {packageCTA}
                </Button>
            </div>
            <Pricing
                {...pricingProps}
                fixedHeight={false}
                taxLabel={taxLabel}
            />
            <PackageFeatures
                features={features}
                useMain={includeOtherFeatures}
                shouldShowNewTag={shouldShowNewTag}
            />
        </div>
    );
};

const LearnMoreAction = ({
    secondaryCTA,
    shouldShowLearnMore,
    onLearnMore,
    packagePath,
}) => {
    let learnMoreAction = null;
    const linkClasses =
        'learn-more-link eds-text-bm eds-text-weight--semibold eds-text-color--grey-900 eds-l-pad-vert-8 eds-l-pad-hor-12';

    if (shouldShowLearnMore && secondaryCTA) {
        learnMoreAction = (
            <div className="package-secondary-cta">
                <div className="learn-more-container">
                    <Button
                        type="link"
                        style="anchor"
                        onClick={onLearnMore}
                        href={packagePath}
                        __containerClassName={linkClasses}
                    >
                        <span className="eds-l-mar-right-1">
                            {secondaryCTA}
                        </span>
                        <Icon
                            type={<ArrowRightChunky />}
                            color="ui-orange"
                            size="xsmall"
                            __containerClassName="learn-more-icon"
                        />
                    </Button>
                </div>
            </div>
        );
    }

    return <div>{learnMoreAction}</div>;
};

export default class Package extends PureComponent {
    static propTypes = {
        ...PRICING_PROP_TYPES,
        description: translationPropType.isRequired,
        name: translationPropType.isRequired,
        includeOtherFeatures: PropTypes.bool,
        features: PropTypes.arrayOf(translationPropType),
        primaryCTA: translationPropType,
        secondaryCTA: translationPropType,
        copyPrimaryCTA: translationPropType,
        CTAStyle: PropTypes.string,
        onClick: PropTypes.func,
        shouldShowLearnMore: PropTypes.bool,
        shouldShowNewTag: PropTypes.bool,
        onLearnMore: PropTypes.func,
        smallScreen: PropTypes.bool,
        isCopy: PropTypes.bool,
        dataAutomation: PropTypes.string,
        onPackageSelectionClick: PropTypes.func,
        packagePath: PropTypes.string,
        isProminentPackage: PropTypes.bool,
        taxLabel: ASSORTMENT_TAX_PROP_TYPES,
    };

    render() {
        const {
            description,
            name,
            includeOtherFeatures,
            features,
            primaryCTA,
            secondaryCTA,
            copyPrimaryCTA,
            CTAStyle,
            onClick,
            onLearnMore,
            smallScreen,
            isCopy,
            dataAutomation,
            packagePath,
            isProminentPackage,
            logoBgColor,
            logoImg,
            logoWidth,
            logoHeight,
            taxLabel,
            shouldShowLearnMore = true,
            shouldShowNewTag,
            ...props
        } = this.props;

        const pricingProps = _.pick(props, PRICING_PROPS);
        const className = classnames(
            'package-card eds-g-cell',
            'eds-fx--fade-in eds-fx--delay-2',
            {
                'package-card__offset': isProminentPackage && !smallScreen,
                'package-card__small eds-l-mar-all-6': smallScreen,
                'eds-g-cell-1-3': !smallScreen,
            },
        );

        return (
            <section className={className}>
                <BasePackage
                    {...pricingProps}
                    alignCenter={smallScreen}
                    description={description}
                    name={name}
                    smallScreen={smallScreen}
                    logoBgColor={logoBgColor}
                    logoImg={logoImg}
                    logoWidth={logoWidth}
                    logoHeight={logoHeight}
                    isProminentPackage={isProminentPackage}
                    isSmallScreen={smallScreen}
                    taxLabel={taxLabel}
                />
                <PackageDetail
                    copyPrimaryCTA={copyPrimaryCTA}
                    CTAStyle={CTAStyle}
                    features={features}
                    includeOtherFeatures={includeOtherFeatures}
                    isCopy={isCopy}
                    onClick={onClick}
                    onLearnMore={onLearnMore}
                    primaryCTA={primaryCTA}
                    secondaryCTA={secondaryCTA}
                    dataAutomation={dataAutomation}
                    packagePath={packagePath}
                    pricingProps={pricingProps}
                    taxLabel={taxLabel}
                    shouldShowNewTag={shouldShowNewTag}
                />
                <LearnMoreAction
                    secondaryCTA={secondaryCTA}
                    shouldShowLearnMore={shouldShowLearnMore}
                    onLearnMore={onLearnMore}
                    packagePath={packagePath}
                />
            </section>
        );
    }
}
